import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import useAppointment from '../../../../../hooks/useAppointment';
import { Appointment, AppointmentStateEnum } from '../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import VyButton from '../../../../common/VyButton';
import VyModal from '../../../../common/VyModal';

interface AppointmentValidationModalProps {
  appointment: Appointment;
  onClose: () => void;
  bookingmode: boolean;
  boothId?: string;
}
const AppointmentValidationModal = ({ appointment, onClose, bookingmode, boothId }: AppointmentValidationModalProps) => {
  const [bookingError, setBookingError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation(['appointment', 'eventhome']);
  const router = useRouter();
  const user = useSelector((state) => state.common.user);
  const event = useSelector((state) => state.admin.event || state.common.event);
  const { book: bookAppointment, cancel: cancelAppointment, _delete: deleteAppointment, getAppointmentState } = useAppointment(boothId, !bookingmode);

  async function book() {
    bookAppointment(
      appointment,
      () => {
        setIsLoading(false);
        onClose();
      },
      () => {
        setBookingError(t('appointment:limit-error'));
      },
      () => {
        setBookingError(t('appointment:errors.booking-overlap'));
      }
    );
  }

  function unbook() {
    cancelAppointment(appointment, () => {
      setIsLoading(false);
      onClose();
    });
  }

  function _delete() {
    deleteAppointment(
      appointment,
      () => {
        setIsLoading(false);
        onClose();
      },
      () => {
        setBookingError('');
      },
      boothId
    );
  }

  function handleSlotClick() {
    if (user) {
      setIsLoading(true);
      if (!bookingmode) _delete();
      else if (bookingmode && !appointment.user) book();
      //@ts-ignore
      // typescrip is ignored here since user is not populated for appointment
      else if (bookingmode && (appointment.user === user?._id || appointment.user?._id === user?._id)) unbook();
    } else if (event?.eventauth?.registerPageUrl) router.push(event?.eventauth?.registerPageUrl);
  }

  return (
    <VyModal disableDefaultStyling open={!!appointment} onClose={onClose}>
      {() => (
        <div className="flex flex-col text-center">
          <span>
            {user && (
              <>
                {getAppointmentState(appointment) === AppointmentStateEnum.AVAILABLE && t('appointment:appointment-confirm')}
                {getAppointmentState(appointment) === AppointmentStateEnum.CAN_UNBOOK && t('appointment:appointment-cancel-confirm')}
                {(getAppointmentState(appointment) === AppointmentStateEnum.NOT_BOOKED ||
                  getAppointmentState(appointment) === AppointmentStateEnum.BOOKED_BY_USER) &&
                  t('appointment:appointment-delete-confirm')}
              </>
            )}
            {!user && t('eventhome:login-message')}
          </span>
          <small style={{ color: 'red' }}>{bookingError}</small>
          {!bookingError && appointment && (
            <div className="flex flex-col space-x-2 md:flex-row">
              <VyButton disabled={isLoading} showLoadingIcon={isLoading} onClick={handleSlotClick}>
                {user ? t('eventhome:yes') : t('eventhome:login')}
              </VyButton>
              <VyButton
                ghost
                disabled={isLoading}
                onClick={() => {
                  onClose();
                }}>
                {t('eventhome:cancel')}
              </VyButton>
            </div>
          )}
        </div>
      )}
    </VyModal>
  );
};

export default AppointmentValidationModal;
